import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../lib/utils";

/** Simple accordion — items = [{q, a}] */
export default function FaqPreview({ items, title = "Frequently Asked Questions" }) {
  const [open, setOpen] = useState(0);
  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="faq-preview"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <Eyebrow>Frequently Asked</Eyebrow>
            <RevealText
              as="h2"
              text={title}
              className="mt-4 font-display font-extrabold text-white text-display"
            />
            <Link
              to="/faqs"
              data-testid="faq-view-all"
              className="mt-8 inline-flex items-center gap-2 text-emerald font-semibold text-sm hover:gap-3 transition-all"
            >
              View All FAQs <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="divide-y divide-charcoal border-y border-charcoal">
              {items.map((it, i) => (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="w-full flex items-center justify-between text-left py-5 md:py-6 gap-6"
                  >
                    <span
                      className={cn(
                        "font-display font-semibold text-lg md:text-xl transition-colors",
                        open === i ? "text-emerald" : "text-white"
                      )}
                    >
                      {it.q}
                    </span>
                    <span className="w-8 h-8 grid place-items-center border border-charcoal shrink-0">
                      {open === i ? (
                        <Minus className="w-4 h-4 text-emerald" />
                      ) : (
                        <Plus className="w-4 h-4 text-white" />
                      )}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 text-mutedtext text-base leading-relaxed max-w-2xl">
                          {it.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
